import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { AI_DESCRIPTION_OFFSET } from '../config';
import { toUrlFromQuery } from '../utils';
import {
  SearchAddressResult,
  SearchAgentResult,
  SearchAgentTeamResult,
} from '../system/search';
import {
  ListingRemarksHistoryResponse,
  ListingRemarksResponse,
  RecommendationResponse,
} from '../system/recommendations';
import { MemberDefaults } from '../system/defaults';
import { useAuth } from './AuthContext';
import { ResoResource } from '../system/reso';
import { ResoQueryBuilder, resource } from '../system/reso-query';
import { useDeleteListingResources } from './util/deleteListingResources';
import { DashboardData } from '../system/analytics';
import { sanitizeTaxId } from '../system/location';
import { useTenant } from './TenantContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';
import { PropertyField } from '../system/field';
import { ProjectionResponse } from '../system/resource';
import {
  ListingContactsRequest,
  ListingContactsResponse,
  ShareDraftRequest,
  ShareDraftResponse,
} from '../components/edit/hooks/useShareDraft/types';
import { isMimeTypeNullError } from '../system/media';
import { SaveAccountResponse, AccountMetadata } from '../system/account';

export interface ServiceContextType {
  get: <T>(
    endpoint: string,
    query: Record<string, any>,
    hideError?: boolean,
    signal?: AbortSignal,
  ) => Promise<T>;
  post: <T>(
    endpoint: string,
    query: Record<string, any>,
    body: Record<string, any>,
  ) => Promise<T>;
  searchLocation: (
    query: string,
    latitude: number,
    longitude: number,
    address?: {
      houseNumber: string;
      street?: string;
      city?: string;
      county?: string;
      state?: string;
      zipcode?: string;
      unitNumber?: string;
    },
  ) => Promise<SearchAddressResult | undefined>;
  releaseListing: (listingKey: string) => Promise<any>;
  searchLocationByTaxId: (
    taxId: string,
    county?: string,
  ) => Promise<SearchAddressResult[]>;
  searchLocationByListingId: (
    listingId: string,
  ) => Promise<SearchAddressResult[]>;
  saveMedia: (preparedFiles: any, listingKey: any) => Promise<any>;
  getSuggestions: (
    addressResult: SearchAddressResult,
  ) => Promise<RecommendationResponse>;
  deleteListingResource: (
    listingKey: string,
    resourceName: string,
    resourceKey: string,
  ) => Promise<void>;
  findAgent: (
    agentId: string,
    isScopedToBrokerage?: boolean,
    signal?: AbortSignal,
  ) => Promise<SearchAgentResult[]>;
  findAgentTeam: (memberKey: string) => Promise<SearchAgentTeamResult>;
  getListingRemarksHistory: (
    listingKey: string,
  ) => Promise<ListingRemarksHistoryResponse>;
  setGeneratedRemark: (isGenerated: boolean) => void;
  isRemarkGenerated: () => boolean;
  getListingRemarks: (
    property: { [key: string]: any },
    tone: string,
    perspective: string,
    size: string,
  ) => Promise<ListingRemarksResponse>;
  getListingResources: (listingKey: any, resourceType: any) => Promise<any>;
  getLiveValues: (
    listingKey: string,
    translate: boolean,
    fields: PropertyField[],
  ) => Promise<ProjectionResponse>;
  deleteListingResources: (
    listingKey: number,
    resources: Array<{
      type: string;
      itemNumber?: number;
      itemKey?: number;
    }>,
    useServiceLayer?: boolean,
  ) => Promise<
    Array<{
      type: string;
      itemNumber?: number;
      itemKey?: number;
      success: boolean;
      notFound?: boolean;
      error?: {
        message: string;
        code?: string;
      };
    }>
  >;
  saveListingResources: (
    listingKey: number | string,
    resources: Array<{
      resource: any;
      resourceType: string;
    }>,
  ) => Promise<
    Array<{
      resource: any;
      resourceType: string;
    }>
  >;
  resource: <T extends ResoResource>(resource: T) => ResoQueryBuilder<T>;
  executeQuery: <T extends ResoResource, R = any>(
    builder: ResoQueryBuilder<T>,
  ) => Promise<{
    value: R[];
    count?: number;
    nextLink?: string;
  }>;
  generateAIDescriptionForImage: (
    listingKey: string,
    url: string,
    maxLength?: number,
  ) => Promise<{ description: string; url: string }>;
  generateObjectsForImage: (
    listingKey: string,
    url: string,
  ) => Promise<{ description: string; url: string }>;
  generateRentSpreeLeaseLink: (payload: {
    propertyID: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    unitNumber?: string;
    agentEmail: string;
  }) => Promise<any>;
  generateShowingProviderLink: (payload: {
    property: { [key: string]: any };
  }) => Promise<any>;
  isGeneratingLeaseLink: () => boolean;
  setGeneratingLeaseLink: (isGenerating: boolean) => void;
  getAnalyticsDashboard: (
    timeframe?: string,
    refresh?: boolean,
  ) => Promise<DashboardData>;
  saveListing: (
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    listingKey: string | undefined,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
  ) => Promise<{
    ListingKey?: string;
    ListingId?: string;
    error?: string;
    [key: string]: any;
  }>;
  publishListing: (
    listingKey: string,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
  ) => Promise<any>;
  getListing: (listingKey: string) => Promise<{
    LiveListing: boolean;
    Property?: { [key: string]: any };
    OpenHouse?: any[];
    Media?: any[];
    Unit?: any[];
    Room?: any[];
    GreenVerification?: any[];
  }>;
  getMemberDefaults: () => Promise<MemberDefaults>;
  saveMemberDefaults: (defaults: MemberDefaults) => Promise<string | undefined>;
  deleteMemberDefaults: (
    defaults: MemberDefaults,
  ) => Promise<string | undefined>;
  getListingContacts: (
    request: ListingContactsRequest,
  ) => Promise<ListingContactsResponse>;
  sendShareDraft: (request: ShareDraftRequest) => Promise<ShareDraftResponse>;
  searchListings: (query?: string) => Promise<{
    ResponseStatus: {
      StatusCode: string;
      StatusMessage: string;
    };
    ResponseString: {
      Listings: any[];
    };
  }>;
  searchListingsPaginated: (
    request: {
      page: number;
      perPage: number;
      filters?: {
        propertyType?: string;
        mlsStatus?: string;
        search?: string;
        [key: string]: any;
      };
    },
    onSummary?: (response: any) => void,
    onDetail?: (response: any) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ) => Promise<void>;
  isWebSocketReady: () => boolean;
  // Generic request operation tracking
  registerRequestOperation: (promise: Promise<any>) => void;
  isRequestInProgress: () => boolean;
  getCurrentRequestPromise: () => Promise<any> | null;
  clearRequestOperation: () => void;
  // Media sync error handler registration
  registerMediaSyncErrorHandler?: (handler: (error: Error) => void) => void;
  saveAccount: (metadata: AccountMetadata) => Promise<SaveAccountResponse>;
}

export const ServiceContext = createContext<ServiceContextType | undefined>(
  undefined,
);

export const ServiceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { api } = useTenant();
  const { session } = useAuth();
  const { deleteListingResources: deleteResourcesFromReso } =
    useDeleteListingResources();
  const { hasFeature } = useFeatureFlags();

  const useWebSockets = hasFeature(FeatureFlags.NGW_USE_WEBSOCKETS);
  const isMediaSyncEnabled = hasFeature(FeatureFlags.NGW_MEDIA_FAILURE_MODAL);
  const webSocket = useWebSocket(useWebSockets);

  // Generic request operation tracking
  const currentRequestPromiseRef = useRef<Promise<any> | null>(null);
  const requestInProgressRef = useRef<boolean>(false);

  // Media sync error handler
  const [mediaSyncErrorHandler, setMediaSyncErrorHandler] = useState<
    ((error: Error) => void) | null
  >(null);

  const agentCache = useRef<
    Record<
      string,
      {
        timestamp: number;
        results: SearchAgentResult[];
      }
    >
  >({});

  const remarksHistoryCache = useRef<ListingRemarksHistoryResponse | undefined>(
    undefined,
  );
  const remarkGenerated = useRef<boolean>(false);
  const isGeneratingLeaseLinkRef = useRef<boolean>(false);
  const isGeneratingShowingProviderLinkRef = useRef<boolean>(false);

  const setGeneratingLeaseLink = (generating: boolean) => {
    isGeneratingLeaseLinkRef.current = generating;
  };

  const setGeneratingShowingProviderLink = (generating: boolean) => {
    isGeneratingShowingProviderLinkRef.current = generating;
  };

  const generateRentSpreeLeaseLink = async (payload: {
    propertyID: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    unitNumber?: string;
    agentEmail: string;
  }) => {
    if (isGeneratingLeaseLinkRef.current) {
      throw new Error('Lease link generation already in progress');
    }

    try {
      setGeneratingLeaseLink(true);
      return await post<any>(
        'vendor/rentspree',
        {},
        {
          task: 'generate_lease',
          payload,
        },
      );
    } finally {
      setGeneratingLeaseLink(false);
    }
  };

  const generateShowingProviderLink = async (payload: {
    property: {
      [key: string]: any;
    };
  }) => {
    try {
      setGeneratingShowingProviderLink(true);
      return await post<any>(
        'vendor/showing',
        {},
        { action: 'get-showing-link', property: payload.property },
      );
    } finally {
      setGeneratingShowingProviderLink(false);
    }
  };

  async function handleHTTPError(
    response: Response,
    hideError: boolean = false,
  ) {
    if (!response.ok) {
      let R;
      try {
        R = await response.json();
        if (!hideError) {
          console.error(R);
        }
      } catch (e) {
        R = new Error(`HTTP error! status: ${response.status}`);
      }
      if (!hideError) {
        throw R;
      }
    }
  }

  const get = async <T,>(
    endpoint: string,
    query: Record<string, any>,
    hideError: boolean = false,
    signal?: AbortSignal,
  ): Promise<T> => {
    try {
      const response = await fetch(
        toUrlFromQuery(`${api}/${endpoint}`, {
          ...query,
          system: session?.user.tenant,
        }),
        {
          credentials: 'include',
          signal,
        },
      );

      await handleHTTPError(response, hideError);

      return (await response.json()) as T;
    } catch (error) {
      // Special handling for abort errors
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error; // Re-throw abort errors
      }
      if (!hideError)
        // Re-throw any error that was caught, whether from the API or network
        throw error;
      return null as T;
    }
  };

  const post = async <T,>(
    endpoint: string,
    query: Record<string, any>,
    body: Record<string, any>,
    hideError: boolean = false,
  ): Promise<T> => {
    const response = await fetch(
      toUrlFromQuery(`${api}/${endpoint}`, {
        ...query,
        system: session?.user.tenant,
      }),
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      },
    );

    await handleHTTPError(response, hideError);

    return (await response.json()) as T;
  };

  const getAnalyticsDashboard = async (
    // Amount of time in months to scope the data too (applied within cloudwatch queries)
    timeframe: string = '6',
    // Here we can force the backend route to re-fetch the analytic data from AWS
    refresh: boolean = false,
  ): Promise<DashboardData> => {
    try {
      return await get<DashboardData>('get-analytics', {
        type: 'dashboard',
        timeframe,
        refresh: refresh ? 'true' : 'false',
      });
    } catch (error) {
      console.error('Error fetching analytics dashboard:', error);
      throw error;
    }
  };

  const searchLocationByTaxId = async (
    taxId: string,
    county?: string,
  ): Promise<SearchAddressResult[]> => {
    const sanitizedTaxId = sanitizeTaxId(taxId);

    return await get(`search-address-tax`, { taxId: sanitizedTaxId, county });
  };

  const searchLocationByListingId = async (
    listingId: string,
  ): Promise<SearchAddressResult[]> => {
    return await get(`search-address-listing-id`, { listingId });
  };

  const searchLocation = async (
    query: string,
    latitude: number,
    longitude: number,
    address?: {
      street?: string;
      houseNumber?: string;
      city?: string;
      county?: string;
      state?: string;
      zipcode?: string;
      unitNumber?: string;
    },
  ): Promise<SearchAddressResult | undefined> => {
    if (!address?.houseNumber) {
      return {
        Address: {
          Street: address?.street || '',
          City: address?.city || '',
          State: address?.state || '',
          Zip: address?.zipcode || '',
          Fips: -1,
          ParcelId: -1,
          County: address?.county || '',
          Latitude: latitude,
          Longitude: longitude,
          UnitNumber: address?.unitNumber,
        },
        Listings: [],
      };
    }
    return await get<SearchAddressResult>('search-address', {
      query,
      latitude,
      longitude,
      address_house_number: address?.houseNumber,
      address_street: address?.street,
      address_city: address?.city,
      address_county: address?.county,
      address_state: address?.state,
      address_zipcode: address?.zipcode,
      address_unit_number: address?.unitNumber,
    });
  };

  const saveMedia = async (preparedFiles: any, listingKey: any) => {
    const body = {
      listingKey: listingKey,
      translate: true,
      files: preparedFiles,
    };
    return await post('save-listing-media', {}, body);
  };

  const getSuggestions = async (
    addressResult: SearchAddressResult,
  ): Promise<RecommendationResponse> => {
    const listingIds = addressResult.Listings.map(
      (listing) => listing.ListingId,
    ).join(',');

    return await get<RecommendationResponse>('get-suggestions', {
      address_street: addressResult.Address?.Street,
      address_city: addressResult.Address?.City,
      address_county: addressResult.Address?.County,
      address_state: addressResult.Address?.State,
      address_zipcode: addressResult.Address?.Zip,
      address_fips: addressResult.Address?.Fips,
      address_latitude: addressResult.Address?.Latitude,
      address_longitude: addressResult.Address?.Longitude,
      tax_id: addressResult.TaxRecord?.TaxId,
      parcel_id: addressResult.TaxRecord?.ParcelId,
      listing_ids: listingIds,
      translate: true,
    });
  };

  const executeQuery = async <T extends ResoResource, R = any>(
    builder: ResoQueryBuilder<T>,
  ): Promise<{
    value: R[];
    count?: number;
    nextLink?: string;
  }> => {
    try {
      const response = await fetch(
        `${api}/search-resource?resource=${builder.getOptions().resource}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(builder.getOptions()),
        },
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      return response.json();
    } catch (error: any) {
      console.error('Search resource error:', error);
      throw error;
    }
  };

  const findAgent = async (
    agentId: string,
    isScopedToBrokerage: boolean = false,
    signal?: AbortSignal,
  ): Promise<SearchAgentResult[]> => {
    // Create a unique cache key
    const cacheKey = `${agentId}-${isScopedToBrokerage}`;
    const CACHE_EXPIRATION = 20 * 60 * 1000; // 20 minutes

    // Check if result is in cache and not expired
    const cachedResult = agentCache.current[cacheKey];
    if (
      cachedResult &&
      Date.now() - cachedResult.timestamp < CACHE_EXPIRATION
    ) {
      return cachedResult.results;
    }

    // Perform the actual agent search
    const results = await get<SearchAgentResult[]>(
      'search-agent',
      {
        query: agentId,
        isScopedToBrokerage,
      },
      false,
      signal,
    );

    // Cache the results with a timestamp
    agentCache.current[cacheKey] = {
      timestamp: Date.now(),
      results,
    };

    return results;
  };

  const findAgentTeam = async (
    memberKey: string,
    signal?: AbortSignal,
  ): Promise<SearchAgentTeamResult> => {
    const results = await get<SearchAgentTeamResult>(
      'search-agent-team',
      {
        memberKey,
      },
      false,
      signal,
    );

    return results;
  };

  const getListingRemarksHistory = async (
    listingKey: string,
  ): Promise<ListingRemarksHistoryResponse> => {
    if (remarksHistoryCache.current) {
      return Promise.resolve(remarksHistoryCache.current);
    }
    try {
      const remarks = await get<ListingRemarksHistoryResponse>(
        'get-listing-remarks-history',
        { listingKey, translate: true },
        false,
      );
      remarksHistoryCache.current = remarks;
      return remarks;
    } catch (e) {
      const remarks: ListingRemarksHistoryResponse = {
        listingid: listingKey,
        remarkshistory: [],
      };
      remarksHistoryCache.current = remarks;
      return remarks;
    }
  };

  const deleteListingResource = async (
    listingKey: string,
    resourceName: string,
    resourceKey: string,
  ): Promise<void> => {
    await get('delete-listing-resource', {
      listingKey,
      resourceName,
      resourceKey,
    });
  };

  const getListingRemarks = async (
    Property: { [key: string]: any },
    tone: string,
    perspective: string,
    size: string,
  ): Promise<ListingRemarksResponse> => {
    return await post<ListingRemarksResponse>(
      'get-listing-remarks',
      { translate: true, tone, perspective, size },
      { Property },
    );
  };

  const getListingResources = async (listingKey: any, resourceType: any) => {
    return await get<any>('get-listing-resources', {
      listingKey,
      translate: true,
      resourceType: resourceType,
    });
  };

  const getLiveValues = async (
    listingKey: string,
    translate: boolean,
    fields: PropertyField[],
  ): Promise<ProjectionResponse> => {
    const request = {
      Property: fields,
    };
    try {
      return await post<ProjectionResponse>(
        'get-listing-values',
        {
          listingKey,
          translate,
        },
        request,
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const releaseListing = async (listingKey: string) => {
    return await get('release-listing', { listingKey });
  };

  const deleteListingResources = useCallback(
    async (
      listingKey: number,
      resources: Array<{
        type: string;
        itemNumber?: number;
        itemKey?: number;
      }>,
      useServiceLayer: boolean = false,
    ) => {
      if (useServiceLayer) {
        // You can use the service layer for item number translation
        return await post<any>(
          'delete-listing-resources',
          { translate: true },
          { listingKey, resources },
        );
      }
      try {
        const results = await deleteResourcesFromReso(
          listingKey,
          resources as any,
        );
        return results;
      } catch (error) {
        console.error('Error deleting listing resources:', error);
        throw error;
      }
    },
    [deleteResourcesFromReso],
  );

  const saveListingResources = async (
    listingKey: number | string,
    resources: Array<{
      resourceType: string;
      resource?: any;
    }>,
  ) => {
    return await post<any>(
      'save-listing-resources',
      {},
      { listingKey, resources, translate: true },
    );
  };

  const generateAIDescriptionForImage = async (
    listingKey: string,
    url: string,
    maxLength: number = 50,
  ): Promise<{ description: string; url: string }> => {
    try {
      const result = await get<{ description: string; url: string }>(
        'generate-image-description',
        // Subtract an offset from the requested max length to account for potential model output variability
        // The AI_DESCRIPTION_OFFSET (10) was provided by Data Science team based on output analysis
        // of the current LLM model. This value may need adjustment if the underlying model changes
        // or if different behavior is observed in production.
        { listingKey, url, maxLength: maxLength - AI_DESCRIPTION_OFFSET },
        false,
      );

      return result;
    } catch (error) {
      console.error('Error generating AI description:', error);
      throw error;
    }
  };

  const generateObjectsForImage = async (
    listingKey: string,
    url: string,
  ): Promise<{ description: string; url: string }> => {
    try {
      const result = await get<{ description: string; url: string }>(
        'generate-image-objects',
        { listingKey, url },
        false,
      );

      return result;
    } catch (error) {
      console.error('Error generating AI Objects:', error);
      throw error;
    }
  };

  const saveListing = async (
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    listingKey: string | undefined,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
  ): Promise<{
    ListingKey?: string;
    ListingId?: string;
    error?: string;
    [key: string]: any;
  }> => {
    listing.Media?.forEach((media: any) => {
      if (media.MEDIA_KEY === null) {
        console.error(`MEDIA_KEY is null for save listing: ${listingKey}`);
        throw new Error(`MEDIA_KEY is null for save listing: ${listingKey}`);
      }
    });

    const savePromise = (async () => {
      try {
        if (useWebSockets) {
          return await webSocket.sendMessageAndWait('LISTING.SAVE', {
            action,
            listingKey,
            listing,
          });
        }
        return await post<any>(
          'save-listing',
          {},
          {
            action,
            listingKey,
            listing,
          },
        );
      } catch (error) {
        console.error('saveListing failed:', error);

        if (isMediaSyncEnabled && isMimeTypeNullError(error)) {
          mediaSyncErrorHandler?.(error as Error);
        } else {
          throw error;
        }
      }
    })();

    // Register this request operation
    registerRequestOperation(savePromise);

    return savePromise;
  };

  const registerMediaSyncErrorHandler = useCallback(
    (handler: (error: Error) => void) => {
      setMediaSyncErrorHandler(() => handler);
    },
    [],
  );

  const publishListing = async (
    listingKey: string,
    listing: {
      Property?: { [key: string]: any };
      OpenHouse?: any[];
      Media?: any[];
      Unit?: any[];
      Room?: any[];
      GreenVerification?: any[];
    },
  ): Promise<any> => {
    if (useWebSockets) {
      return await webSocket.sendMessageAndWait('LISTING.PUBLISH', {
        action: 'PUBLISH',
        listingKey,
        listing,
      });
    }
    return await post<any>(
      'save-listing',
      {},
      {
        action: 'PUBLISH',
        listingKey,
        listing,
      },
    );
  };

  const getListing = async (listingKey: string) => {
    try {
      return await get<any>('get-listing', {
        listingKey,
        translate: true,
        checkout: true,
      });
    } catch (error) {
      console.error('Error loading listing:', error);
      throw error;
    }
  };

  const getListingContacts = async (request: ListingContactsRequest) => {
    try {
      return await post<ListingContactsResponse>(
        'get-listing-contacts',
        {},
        request,
      );
    } catch (error) {
      console.error('Error getting listing contacts:', error);
      throw error;
    }
  };

  const sendShareDraft = async (
    request: ShareDraftRequest,
  ): Promise<ShareDraftResponse> => {
    try {
      return await post<ShareDraftResponse>('share-draft', {}, request);
    } catch (error) {
      console.error('Error sending share draft:', error);
      throw error;
    }
  };

  const getMemberDefaults = async (): Promise<MemberDefaults> => {
    try {
      const defaults = await get<MemberDefaults>('get-defaults', {});

      // remove offer management provider default if the flag is turned off
      // to prevent accidentally populating it on the Property
      if ('OFFER_MANAGEMENT_PROVIDER' in defaults) {
        if (!hasFeature(FeatureFlags.NGW_OFFER_MANAGEMENT)) {
          await deleteMemberDefaults({
            OFFER_MANAGEMENT_PROVIDER: null,
          });

          delete defaults.OFFER_MANAGEMENT_PROVIDER;
        }
      }

      return defaults;
    } catch (error) {
      console.error('Error retrieving member defaults', error);
      throw error;
    }
  };

  const saveMemberDefaults = async (
    defaults: MemberDefaults,
  ): Promise<string | undefined> => {
    try {
      const res = await post<{ message: string; results: any }>(
        'save-defaults',
        {},
        { defaults },
      );
      return res.message;
    } catch (error) {
      console.error('Error saving member defaults', error);
    }
  };

  const deleteMemberDefaults = async (
    defaults: MemberDefaults,
  ): Promise<string | undefined> => {
    try {
      const res = await post<{ message: string }>(
        'delete-defaults',
        {},
        { defaults },
      );
      return res.message;
    } catch (error) {
      console.error('Error deleting member defaults', error);
    }
  };

  const isRemarkGenerated = () => remarkGenerated.current ?? false;
  const setGeneratedRemark = (isRemarkGenerated: boolean) =>
    (remarkGenerated.current = isRemarkGenerated);

  // Generic request operation tracking functions
  const registerRequestOperation = useCallback((promise: Promise<any>) => {
    requestInProgressRef.current = true;
    currentRequestPromiseRef.current = promise;

    // Clear the operation when it completes (success or failure)
    promise.finally(() => {
      requestInProgressRef.current = false;
      currentRequestPromiseRef.current = null;
    });
  }, []);

  const isRequestInProgress = useCallback(() => {
    return requestInProgressRef.current;
  }, []);

  const getCurrentRequestPromise = useCallback(() => {
    return currentRequestPromiseRef.current;
  }, []);

  const clearRequestOperation = useCallback(() => {
    requestInProgressRef.current = false;
    currentRequestPromiseRef.current = null;
  }, []);

  /**
   * Get listings via WebSocket if ready, otherwise return sample data
   */
  const searchListings = useCallback(
    async (query?: string) => {
      try {
        // Check if WebSocket is connected and ready
        if (useWebSockets && webSocket.isConnected) {
          // Fetch via WebSocket
          const response = await webSocket.sendMessageAndWait(
            'LISTING.SEARCH',
            {
              query: query,
            },
            30000,
          );
          return response;
        } else {
          // Return sample data if WebSocket not ready or not enabled
          console.log('WebSocket not ready, using sample data');
        }
      } catch (error) {
        console.error(
          'Error fetching listings, falling back to sample data:',
          error,
        );
      }
    },
    [useWebSockets, webSocket],
  );

  /**
   * Search listings with pagination via WebSocket
   */
  const searchListingsPaginated = useCallback(
    async (
      request: {
        page: number;
        perPage: number;
        filters?: {
          propertyType?: string;
          mlsStatus?: string;
          search?: string;
          [key: string]: any;
        };
      },
      onSummary?: (response: any) => void,
      onDetail?: (response: any) => void,
      onComplete?: () => void,
      onError?: (error: Error) => void,
    ) => {
      try {
        // Check if WebSocket is connected and ready
        if (!useWebSockets || !webSocket.isConnected) {
          const err = new Error('WebSocket is not connected');
          console.error('WebSocket not connected for paginated search');
          if (onError) onError(err);
          throw err;
        }

        // Generate request ID
        const requestId = `LISTING.SEARCH-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Build the payload
        const payload = {
          page: request.page,
          perPage: request.perPage,
          filters: request.filters || {},
        };

        // Send the request with progress handlers
        await webSocket.sendMessageWithProgressHandlers(
          {
            action: 'LISTING.SEARCH',
            requestId,
            payload,
          },
          {
            onProgress: (data: any) => {
              // Route progressive responses based on status field
              if (data.status === 'listing_summary' && onSummary) {
                onSummary(data);
              } else if (data.status === 'listing_detail' && onDetail) {
                onDetail(data);
              }
            },
            onComplete: () => {
              if (onComplete) onComplete();
            },
            onError: (error: Error) => {
              console.error('Paginated listing search error:', error);
              if (onError) onError(error);
            },
          },
          30000, // 30 second timeout
        );
      } catch (error) {
        console.error('Error in paginated listing search:', error);
        const err =
          error instanceof Error
            ? error
            : new Error('Failed to search listings');
        if (onError) onError(err);
        throw err;
      }
    },
    [useWebSockets, webSocket],
  );

  /**
   * Check if WebSocket is connected and ready
   */
  const isWebSocketReady = useCallback(() => {
    return useWebSockets && webSocket.isConnected;
  }, [useWebSockets, webSocket.isConnected]);

  /**
   * Update the current user's account information
   */
  const saveAccount = async (
    metadata: AccountMetadata,
  ): Promise<SaveAccountResponse> => {
    try {
      return await post<SaveAccountResponse>('save-account', {}, { metadata });
    } catch (error) {
      console.error('Error saving account:', error);
      throw error;
    }
  };

  return (
    <ServiceContext.Provider
      value={{
        get,
        post,
        searchLocation,
        searchLocationByTaxId,
        searchLocationByListingId,
        saveMedia,
        getSuggestions,
        releaseListing,
        findAgent,
        findAgentTeam,
        isRemarkGenerated,
        setGeneratedRemark,
        generateAIDescriptionForImage,
        generateObjectsForImage,
        getListingRemarksHistory,
        getListingRemarks,
        getListingResources,
        getListingContacts,
        getLiveValues,
        generateRentSpreeLeaseLink,
        generateShowingProviderLink,
        isGeneratingLeaseLink: () => isGeneratingLeaseLinkRef.current,
        setGeneratingLeaseLink,
        saveListingResources,
        deleteListingResource,
        deleteListingResources,
        getAnalyticsDashboard,
        getMemberDefaults,
        saveMemberDefaults,
        deleteMemberDefaults,
        resource: resource,
        executeQuery,
        saveListing,
        publishListing,
        sendShareDraft,
        getListing,
        searchListings,
        searchListingsPaginated,
        isWebSocketReady,
        // Generic request operation tracking
        registerRequestOperation,
        isRequestInProgress,
        getCurrentRequestPromise,
        clearRequestOperation,
        // Media sync error handler
        registerMediaSyncErrorHandler,
        saveAccount,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export const useService = () => {
  const context = useContext(ServiceContext);
  if (context === undefined) {
    throw new Error('useService must be used within a ServiceProvider');
  }
  return context;
};

export interface ServiceError {
  error: string;
  details?: string;
  info?: {
    code?: string;
    message?: string;
    details?: Array<{
      code: string;
      message: string;
      target: string;
    }>;
    [key: string]: any;
  };
}

export const serviceResponseMutationErrorHandler = (error: any): any => {
  // If it's an axios error with response data
  if (error.response?.data) {
    // If the response data already matches our ServiceError interface
    if (error.response.data.error) {
      throw error.response.data;
    }

    // If it's a plain error message
    const serviceError: ServiceError = {
      error: error.response.data.message || error.response.data,
      details: error.response.statusText,
      info: {
        code: error.response.status.toString(),
        message: error.response.data,
      },
    };
    throw serviceError;
  }

  // If it's a fetch response error that needs parsing
  if (error instanceof Response) {
    return error.json().then((data: any) => {
      throw data;
    });
  }

  // For any other type of error
  const serviceError: ServiceError = {
    error: error.message || 'Unknown Error',
    details: error.stack,
    info: {
      code: 'UNKNOWN',
      message: error.toString(),
    },
  };
  throw serviceError;
};

// Type guard to check if an error matches our ServiceError interface
export const isServiceError = (error: any): error is ServiceError => {
  return error && typeof error === 'object' && 'error' in error;
};
export const formatErrorDetails = (error: ServiceError): string => {
  if (error.info?.details && Array.isArray(error.info.details)) {
    return error.info.details
      .map((detail) => `${detail.message} (${detail.target})`)
      .join('\n');
  }
  return error.details || error.info?.message || error.error;
};
